const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : '/api';

// 獲取認證頭部（暫時不使用認證）
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  // 'Authorization': `Bearer ${JWT_TOKEN}`,  // 暫時註釋掉
});

export interface Sensor {
  id: number;
  name: string;
  type: string;
  location: string;  // 保持向後兼容
  location_name: string;  // 新增：使用者定義的位置名稱
  coordinates: [number, number];
  status: string;
  alert: string;
  battery: number;
  ip: string;
  lastUpdate: string;
  image: string;
  rtsp_url?: string | null;  // RTSP 串流 URL
  ip_address?: string | null;  // IP 攝影機 IP 地址
  ipcam_username?: string | null;  // IP 攝影機使用者名稱（密碼不返回給前端）
  // RGB 攝影機事件影片（當有警報時）
  event_video_url?: string | null;
  event_description?: string | null;
  // 感測器數據屬性
  temperature?: number;
  humidity?: number;
  co_level?: number;
  smoke_level?: number;
  // 地點與攝影機
  site_id?: string | null;
  nvr_camera_id?: string | null;
}

export interface DashboardData {
  sensors: Sensor[];
  total_count: number;
  active_count: number;
  alert_count: number;
}

export interface StatisticsData {
  sensors: StatisticsSensor[];
  alerts: StatisticsAlert[];
  sensor_count: number;
  alert_count: number;
}

export interface StatisticsSensor {
  id: number;
  name: string;
  type: string;
  location_name: string;
  latitude: number;
  longitude: number;
  status: string;
  created_at: string;
  installation_date: string;
}

export interface StatisticsAlert {
  id: number;
  sensor_name: string;
  sensor_type: string;
  location_name: string;
  alert_type: string;
  severity: string;
  status: string;
  message: string;
  triggered_at: string;
  resolved_at: string | null;
}

export interface StatisticsParams {
  start_time: string;
  end_time: string;
  area: string;
  sensor_type: string;
  sync_external?: boolean;
  sync_days?: number;
}

export const api = {
  // 獲取儀表板數據
  async getDashboardData(): Promise<DashboardData> {
    try {
      console.log('Fetching dashboard data...');
      
      // 創建帶超時的 fetch 請求（15秒超時）
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      
      try {
        const response = await fetch(`${API_BASE_URL}/sensors/dashboard/`, {
          headers: getAuthHeaders(),
          signal: controller.signal,
        });
        
        clearTimeout(timeoutId);
        
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error:', errorText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Dashboard data received:', data);
        return data;
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        // 檢查是否為超時錯誤
        if (fetchError.name === 'AbortError') {
          console.error('Request timeout: 請求超過15秒未響應');
          throw new Error('請求超時，請檢查網絡連接或稍後重試');
        }
        throw fetchError;
      }
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      // 如果 API 失敗，返回空數據而不是拋出錯誤
      // 這樣用戶至少可以看到界面，即使沒有數據
      return {
        sensors: [],
        total_count: 0,
        active_count: 0,
        alert_count: 0,
      };
    }
  },

  // 獲取統計數據
  async getStatisticsData(params: StatisticsParams): Promise<StatisticsData> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/statistics/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(params),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Statistics API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Statistics data received:', data);
      return data;
    } catch (error) {
      console.error('Error fetching statistics data:', error);
      throw error;
    }
  },

  // 獲取感測器列表
  async getSensors(): Promise<Sensor[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.results || data;
    } catch (error) {
      console.error('Error fetching sensors:', error);
      return [];
    }
  },

  // 獲取單個感測器詳情
  async getSensorDetail(id: number): Promise<Sensor | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/${id}/`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching sensor detail:', error);
      return null;
    }
  },

  // 獲取感測器閾值設定
  async getSensorThreshold(sensorId: number): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/${sensorId}/threshold/`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching sensor threshold:', error);
      return null;
    }
  },

  // 保存感測器閾值設定
  async saveSensorThreshold(sensorId: number, thresholdData: any): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/${sensorId}/threshold/save/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(thresholdData),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error saving sensor threshold:', error);
      throw error;
    }
  },

  // 永久刪除感測器
  async deleteSensor(sensorId: number): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/delete/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        body: JSON.stringify({ sensor_id: sensorId }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error deleting sensor:', error);
      throw error;
    }
  },


  // 獲取感測器統計信息
  async getSensorStats(sensorId: number): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/${sensorId}/stats/`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching sensor stats:', error);
      throw error;
    }
  },

  // 同步外部感測器數據
  async syncExternalSensor(sensorId: string, days: number = 1): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/external/sync/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sensor_id: sensorId,
          days: days,
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Sync External Sensor Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error syncing external sensor:', error);
      throw error;
    }
  },

  // 獲取外部感測器列表
  async getExternalSensors(): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/external/list/`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching external sensors:', error);
      return { sensors: [], total_count: 0 };
    }
  },

  // 新增感測器
  async createSensor(sensorData: {
    name: string;
    type: string;
    location_name: string;
    latitude: number;
    longitude: number;
    ip?: string;
    // IP 攝影機相關欄位
    ip_address?: string;
    ipcam_username?: string;
    ipcam_password?: string;
    rtsp_url?: string;
    // NVR 攝影機相關欄位
    nvr_host?: string;
    nvr_port?: number;
    nvr_username?: string;
    nvr_password?: string;
    nvr_camera_id?: string;
  }): Promise<any> {
    try {
      const response = await fetch(`${API_BASE_URL}/sensors/create/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(sensorData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error creating sensor:', error);
      throw error;
    }
  },

};